import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SAT, REC, fmt, fmtPct, scoreColor } from './constants';

const SaturationTab = ({ saturation, expandedIcb, toggleLeaders, level }) => {
  const { t } = useTranslation();

  if (!saturation?.data?.length) {
    return (
      <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Alert severity="info" sx={{ m: 2 }}>{t('icb.common.noSaturationData')}</Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('icb.saturationView.title', { count: saturation.count || saturation.data.length, level })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('icb.saturationView.subtitle')}
          </Typography>
        </Box>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {Object.entries(SAT).map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: v.color }} />
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color={v.color}>
                {t(`icb.saturationLevel.${k}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { k: 'rank', a: 'left' }, { k: 'industry', a: 'left' }, { k: 'level', a: 'left' },
                { k: 'score', a: 'right' }, { k: 'globalRank', a: 'right' }, { k: 'stockCount', a: 'right' },
                { k: 'change1D', a: 'right' }, { k: 'change5D', a: 'right' }, { k: 'recommendation', a: 'left' },
              ].map(({ k, a }) => (
                <TableCell key={k} align={a}
                  sx={{ fontWeight: 700, bgcolor: '#f8f9fa', fontSize: '0.75rem', py: 1 }}>
                  {t(`icb.saturationView.headers.${k}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {saturation.data.map((s, idx) => {
              const sc = SAT[s.saturation_level] || SAT.MODERATE;
              const recKey = s.recommendation;
              const recStyle = REC[recKey];
              const recLabel = recStyle ? t(`icb.recommendation.${recKey}`) : recKey;
              const recColor = recStyle ? recStyle.color : '#64748b';
              const isOpen = expandedIcb === s.icb_code;
              return (
                <TableRow
                  key={s.icb_code}
                  hover
                  onClick={() => toggleLeaders(s.icb_code)}
                  sx={{
                    cursor: 'pointer',
                    '&:nth-of-type(odd)': { bgcolor: isOpen ? '#dbeafe' : '#fafafa' },
                    bgcolor: isOpen ? '#dbeafe !important' : undefined,
                    borderLeft: isOpen ? '3px solid #6366f1' : '3px solid transparent',
                    transition: 'background-color 0.15s, border-color 0.15s',
                    '&:hover': { bgcolor: isOpen ? '#bfdbfe !important' : '#eef2ff !important' },
                  }}
                >
                  <TableCell sx={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 700 }}>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.85rem">{s.name_vi || s.name_en || s.icb_code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sc.color, flexShrink: 0 }} />
                      <Typography fontSize="0.8rem" fontWeight={600} color={sc.color}>
                        {t(`icb.saturationLevel.${s.saturation_level || 'MODERATE'}`)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={700} fontSize="0.85rem" color={scoreColor(s.final_score)} sx={{ fontFeatureSettings: '"tnum"' }}>
                      {fmt(s.final_score)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', color: '#64748b' }}>#{s.rank}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', fontFeatureSettings: '"tnum"' }}>{s.stock_count}</TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.8rem" fontWeight={600} color={s.avg_change_1d >= 0 ? '#10b981' : '#ef4444'} sx={{ fontFeatureSettings: '"tnum"' }}>
                      {fmtPct(s.avg_change_1d)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontSize="0.8rem" fontWeight={600} color={s.avg_change_5d >= 0 ? '#10b981' : '#ef4444'} sx={{ fontFeatureSettings: '"tnum"' }}>
                      {fmtPct(s.avg_change_5d)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.8rem" fontWeight={700} color={recColor}>{recLabel}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

SaturationTab.propTypes = {
  saturation: PropTypes.shape({
    count: PropTypes.number,
    data: PropTypes.arrayOf(PropTypes.shape({
      icb_code: PropTypes.string,
      name_vi: PropTypes.string,
      name_en: PropTypes.string,
      saturation_level: PropTypes.string,
      recommendation: PropTypes.string,
      final_score: PropTypes.number,
      rank: PropTypes.number,
      stock_count: PropTypes.number,
      avg_change_1d: PropTypes.number,
      avg_change_5d: PropTypes.number,
    })),
  }),
  expandedIcb: PropTypes.string,
  toggleLeaders: PropTypes.func.isRequired,
  level: PropTypes.string,
};

export default SaturationTab;
